// src/pages/ConstanciaNoAdeudo.js
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaFileAlt, FaUpload, FaPrint, FaDownload, FaChevronLeft, FaTimes, FaSearch, FaBold, FaItalic, FaUnderline, FaAlignLeft, FaAlignCenter, FaAlignRight, FaExpand, FaCompress } from 'react-icons/fa';
import { db, storage } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { collection, query, onSnapshot, orderBy, doc, getDoc, updateDoc, Timestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import './ConstanciaNoAdeudo.css';

const DEFAULT_REDACCION = `{nombreFracc}, por conducto de {presidente}; Presidente de la Mesa Directiva y administrador, del condominio {nombreFracc}, hace constar que la propiedad ubicada en el condominio {numeroCasa} a nombre de el {residente}, sujeto al Régimen de Propiedad en Condominio, ubicado en {direccion}, se encuentra al corriente de sus cuotas de mantenimiento correspondiente al año {año}. Lo anterior para todos los efectos legales a que haya lugar.`;

// Helper para garantizar que siempre trabajamos con un objeto Date válido
const safeDate = (d) => (d instanceof Date && !isNaN(d.getTime())) ? d : new Date();

const ConstanciaNoAdeudo = () => {
    const navigate = useNavigate();
    const { userData } = useAuth();
    const fraccionamientoId = userData?.fraccionamientoId;
    const [casas, setCasas] = useState([]);
    const [selectedCasa, setSelectedCasa] = useState(null);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [fraccionamientoData, setFraccionamientoData] = useState(null);
    const [logoUrl, setLogoUrl] = useState('');
    const [firmaUrl, setFirmaUrl] = useState('');
    const [selloUrl, setSelloUrl] = useState('');
    const [presidenteNombre, setPresidenteNombre] = useState('');
    const [presidenteCargo, setPresidenteCargo] = useState('');
    const [direccionFraccionamiento, setDireccionFraccionamiento] = useState('');
    const [ciudadEstado, setCiudadEstado] = useState('San Juan del Río Querétaro');
    const [uploadingFirma, setUploadingFirma] = useState(false);
    const [uploadingSello, setUploadingSello] = useState(false);
    const [fechaConstancia, setFechaConstancia] = useState(new Date());
    const [tituloConstancia, setTituloConstancia] = useState('CONSTANCIA DE NO ADEUDO');
    const [subtituloConstancia, setSubtituloConstancia] = useState('A QUIEN CORRESPONDA:');
    const [redaccionConstancia, setRedaccionConstancia] = useState('');
    const [editorModalOpen, setEditorModalOpen] = useState(false);
    const [fontSize, setFontSize] = useState(12);
    const [fontFamily, setFontFamily] = useState('Times New Roman');
    const editorRef = useRef(null);

    // Inicializar el contenido del editor cuando se abre
    useEffect(() => {
        if (editorModalOpen && editorRef.current) {
            // Solo establecer el texto si está vacío en el DOM para evitar sobrescribir
            // Pero en este caso queremos que siempre se 'reinicie' con el valor actual del estado
            // al abrir el modal.
            // OJO: Si el usuario ya editó, redaccionConstancia tiene el valor.
            // Si redaccionConstancia tiene variables (porque nunca se editó), resolveTemplate las cambia.
            // Al guardar, redaccionConstancia se actualiza con el texto plano.

            const textoInicial = resolveTemplate(redaccionConstancia || DEFAULT_REDACCION);
            if (editorRef.current.innerText !== textoInicial) {
                editorRef.current.innerText = textoInicial;
            }
        }
    }, [editorModalOpen]); // Se ejecuta cuando se abre el modal

    // Cargar datos del fraccionamiento
    useEffect(() => {
        const loadFraccionamientoData = async () => {
            if (!fraccionamientoId) return;

            try {
                const fraccDoc = await getDoc(doc(db, 'fraccionamientos', fraccionamientoId));
                if (fraccDoc.exists()) {
                    const data = fraccDoc.data() || {};
                    const configRecibos = data.configuracionRecibos || {};

                    // Obtener logo
                    const logo = configRecibos.logoUrl || data.logoUrl || '';
                    setLogoUrl(logo);

                    // Obtener firma y sello guardados
                    const firma = configRecibos.firmaConstanciaUrl || '';
                    const sello = configRecibos.selloConstanciaUrl || '';
                    setFirmaUrl(firma);
                    setSelloUrl(sello);

                    // Obtener nombre del fraccionamiento
                    const nombre = configRecibos.condominioNombre || data.condominioNombre || data.nombre || '';

                    // Obtener dirección
                    const direccion = configRecibos.condominioDireccion || data.condominioDireccion || data.direccion || '';
                    setDireccionFraccionamiento(direccion);

                    // Obtener administrador/presidente
                    const adminNombre = configRecibos.administradorNombre || data.adminNombre || data.administradorNombre || '';
                    setPresidenteNombre(adminNombre);

                    // Cargo por defecto
                    setPresidenteCargo('Presidente y Administrador del Fraccionamiento');

                    // Cargar configuración de constancia guardada
                    const configConstancia = configRecibos.configuracionConstancia || {};
                    if (configConstancia.titulo) setTituloConstancia(configConstancia.titulo);
                    if (configConstancia.subtitulo) setSubtituloConstancia(configConstancia.subtitulo);
                    if (configConstancia.redaccion) setRedaccionConstancia(configConstancia.redaccion);
                    if (configConstancia.fecha) {
                        try {
                            let fechaGuardada = configConstancia.fecha.toDate
                                ? configConstancia.fecha.toDate()
                                : new Date(configConstancia.fecha);
                            // Validar que la fecha resultante sea válida antes de establecerla
                            setFechaConstancia(safeDate(fechaGuardada));
                        } catch {
                            // Si el valor guardado es inválido, usar la fecha actual como fallback
                            setFechaConstancia(new Date());
                        }
                    }
                    if (configConstancia.fontSize) setFontSize(configConstancia.fontSize);
                    if (configConstancia.fontFamily) setFontFamily(configConstancia.fontFamily);

                    setFraccionamientoData({
                        nombre,
                        direccion,
                        adminNombre
                    });
                }
            } catch (error) {
                console.error('Error cargando datos del fraccionamiento:', error);
            }
        };

        loadFraccionamientoData();
    }, [fraccionamientoId]);

    // Cargar casas al corriente (sin adeudo)
    useEffect(() => {
        if (!fraccionamientoId) {
            setLoading(false);
            return;
        }

        const casasRef = collection(db, 'fraccionamientos', fraccionamientoId, 'casas');
        const q = query(casasRef, orderBy('numero'));

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const casasList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            // Filtrar solo casas al corriente (adeudo <= 0)
            const casasAlCorriente = casasList.filter(casa => {
                const adeudo = Number(casa?.adeudo ?? casa?.deuda ?? casa?.montoAdeudo ?? 0);
                return adeudo <= 0;
            });

            setCasas(casasAlCorriente);
            setLoading(false);
        }, (error) => {
            console.error('Error cargando casas:', error);
            setLoading(false);
        });

        return () => unsubscribe();
    }, [fraccionamientoId]);

    const getResidentName = (casa) => {
        return casa?.nombre_residente ||
            casa?.residente ||
            casa?.usuario ||
            casa?.nombreResidente ||
            (casa?.propietario && casa.propietario.nombre) ||
            (casa?.contacto && casa.contacto.nombre) ||
            'Residente no registrado';
    };

    const filteredCasas = casas.filter(casa => {
        const searchLower = searchTerm.toLowerCase();
        const numero = String(casa.numero || '').toLowerCase();
        const nombre = getResidentName(casa).toLowerCase();
        return numero.includes(searchLower) || nombre.includes(searchLower);
    });

    const handleCasaSelect = (casa) => {
        setSelectedCasa(casa);
    };

    const handleFirmaUpload = async (e) => {
        const file = e.target.files[0];
        if (!file || !fraccionamientoId) return;

        setUploadingFirma(true);
        try {
            const fileRef = ref(storage, `fraccionamientos/${fraccionamientoId}/constancias/firma_${Date.now()}.${file.name.split('.').pop()}`);
            await uploadBytes(fileRef, file);
            const url = await getDownloadURL(fileRef);
            setFirmaUrl(url);

            // Guardar URL en Firestore usando notación de puntos (dot notation)
            const fraccRef = doc(db, 'fraccionamientos', fraccionamientoId);
            await updateDoc(fraccRef, {
                'configuracionRecibos.firmaConstanciaUrl': url
            });
        } catch (error) {
            console.error('Error subiendo firma:', error);
            alert('Error al subir la firma. Por favor intenta nuevamente.');
        } finally {
            setUploadingFirma(false);
        }
    };

    const handleSelloUpload = async (e) => {
        const file = e.target.files[0];
        if (!file || !fraccionamientoId) return;

        setUploadingSello(true);
        try {
            const fileRef = ref(storage, `fraccionamientos/${fraccionamientoId}/constancias/sello_${Date.now()}.${file.name.split('.').pop()}`);
            await uploadBytes(fileRef, file);
            const url = await getDownloadURL(fileRef);
            setSelloUrl(url);

            // Guardar URL en Firestore usando notación de puntos
            const fraccRef = doc(db, 'fraccionamientos', fraccionamientoId);
            await updateDoc(fraccRef, {
                'configuracionRecibos.selloConstanciaUrl': url
            });
        } catch (error) {
            console.error('Error subiendo sello:', error);
            alert('Error al subir el sello. Por favor intenta nuevamente.');
        } finally {
            setUploadingSello(false);
        }
    };

    const handleRemoveImage = async (tipo) => {
        if (!fraccionamientoId) return;

        try {
            const fraccRef = doc(db, 'fraccionamientos', fraccionamientoId);

            if (tipo === 'firma') {
                setFirmaUrl('');
                await updateDoc(fraccRef, {
                    'configuracionRecibos.firmaConstanciaUrl': ''
                });
            } else if (tipo === 'sello') {
                setSelloUrl('');
                await updateDoc(fraccRef, {
                    'configuracionRecibos.selloConstanciaUrl': ''
                });
            }
        } catch (error) {
            console.error(`Error eliminando ${tipo}:`, error);
        }
    };

    const formatDate = (date, ciudadEstado) => {
        const months = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
            'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
        const day = date.getDate().toString().padStart(2, '0');
        const month = months[date.getMonth()];
        const year = date.getFullYear();
        return `${ciudadEstado} ${day} de ${month} de ${year}.`;
    };

    const guardarConfiguracionConstancia = async () => {
        if (!fraccionamientoId) return;
        try {
            const fraccRef = doc(db, 'fraccionamientos', fraccionamientoId);
            const fraccData = (await getDoc(fraccRef)).data() || {};
            const configRecibos = fraccData.configuracionRecibos || {};
            await updateDoc(fraccRef, {
                configuracionRecibos: {
                    ...configRecibos,
                    configuracionConstancia: {
                        titulo: tituloConstancia,
                        subtitulo: subtituloConstancia,
                        redaccion: redaccionConstancia,
                        fecha: Timestamp.fromDate(fechaConstancia)
                    }
                }
            });
        } catch (error) {
            console.error('Error guardando configuración de constancia:', error);
        }
    };

    const handleFormatCommand = (command, value = null) => {
        document.execCommand(command, false, value);
    };

    const handleEditorChange = (e) => {
        setRedaccionConstancia(e.target.value);
    };

    const insertVariable = (variable) => {
        // Intentar con el editor contentEditable primero, luego el textarea del formulario
        const editorContent = document.getElementById('editor-content');
        const formTextarea = document.getElementById('redaccion-textarea');

        if (editorContent && document.activeElement === editorContent) {
            // Usar contentEditable
            const selection = window.getSelection();
            if (selection.rangeCount > 0) {
                const range = selection.getRangeAt(0);
                range.deleteContents();
                const textNode = document.createTextNode(`{${variable}}`);
                range.insertNode(textNode);
                range.setStartAfter(textNode);
                range.setEndAfter(textNode);
                selection.removeAllRanges();
                selection.addRange(range);
                setRedaccionConstancia(editorContent.textContent || editorContent.innerText);
            } else {
                editorContent.textContent = (editorContent.textContent || '') + `{${variable}}`;
                setRedaccionConstancia(editorContent.textContent);
            }
        } else if (formTextarea) {
            // Usar textarea normal
            const start = formTextarea.selectionStart;
            const end = formTextarea.selectionEnd;
            const text = redaccionConstancia;
            const newText = text.substring(0, start) + `{${variable}}` + text.substring(end);
            setRedaccionConstancia(newText);
            setTimeout(() => {
                formTextarea.focus();
                const newPosition = start + variable.length + 2;
                formTextarea.setSelectionRange(newPosition, newPosition);
            }, 0);
        } else {
            // Si no hay editor activo, simplemente agregar al final
            setRedaccionConstancia(prev => prev + `{${variable}}`);
        }
    };

    const resolveTemplate = (template) => {
        if (!template) return '';
        if (!selectedCasa || !fraccionamientoData) return template; // Retornar plantilla si no hay datos

        const residenteNombre = getResidentName(selectedCasa);
        const numeroCasa = selectedCasa.numero || 'N/A';
        const nombreFracc = fraccionamientoData.nombre || 'N/A';
        const direccion = direccionFraccionamiento || 'N/A';
        const presidente = presidenteNombre || 'N/A';
        const añoActual = fechaConstancia.getFullYear();

        return template
            .replace(/\{nombreFracc\}/g, nombreFracc)
            .replace(/\{presidente\}/g, presidente)
            .replace(/\{numeroCasa\}/g, numeroCasa)
            .replace(/\{residente\}/g, `C. ${residenteNombre}`)
            .replace(/\{direccion\}/g, direccion)
            .replace(/\{año\}/g, añoActual.toString());
    };

    const generarConstancia = () => {
        if (!selectedCasa || !fraccionamientoData) return null;

        const fechaFormateada = formatDate(fechaConstancia, ciudadEstado);
        const residenteNombre = getResidentName(selectedCasa);
        const numeroCasa = selectedCasa.numero || 'N/A';
        const nombreFracc = fraccionamientoData.nombre || 'N/A';
        const direccion = direccionFraccionamiento || 'N/A';
        const presidente = presidenteNombre || 'N/A';
        const cargo = presidenteCargo || 'Presidente y Administrador del Fraccionamiento';
        const añoActual = fechaConstancia.getFullYear();

        // Obtener texto resuelto (ya sea de la redacción personalizada o por defecto)
        // NOTA: Si redacciónConstancia ya tiene texto, asumimos que ya está "resuelto" si fue editado,
        // pero si contiene variables (por ser antiguo o copiado), intentamos resolverlas.
        let textoCuerpo = resolveTemplate(redaccionConstancia.trim() || DEFAULT_REDACCION);

        return {
            fecha: fechaFormateada,
            residente: residenteNombre,
            numeroCasa,
            nombreFracc,
            direccion,
            presidente,
            cargo,
            titulo: tituloConstancia,
            subtitulo: subtituloConstancia,
            textoCuerpo,
            año: añoActual
        };
    };

    const handlePrint = () => {
        const constancia = generarConstancia();
        // ... rest of handlePrint ...
        if (!constancia) {
            alert('Por favor selecciona un residente primero.');
            return;
        }

        const printWindow = window.open('', '_blank');
        const htmlContent = `
            <!DOCTYPE html>
            <html lang="es">
            <head>
                <meta charset="UTF-8">
                <meta name="viewport" content="width=device-width, initial-scale=1.0">
                <title>Constancia de No Adeudo</title>
                <style>
                    * { margin: 0; padding: 0; box-sizing: border-box; }
                    body {
                        font-family: 'Times New Roman', serif;
                        font-size: 12pt;
                        line-height: 1.6;
                        color: #000;
                        padding: 40px;
                        background: white;
                    }
                    .header {
                        display: flex;
                        justify-content: space-between;
                        align-items: flex-start;
                        margin-bottom: 30px;
                    }
                    .logo {
                        max-width: 150px;
                        max-height: 80px;
                        object-fit: contain;
                    }
                    .fecha {
                        text-align: right;
                        font-size: 11pt;
                    }
                    .titulo {
                        text-align: center;
                        font-size: 16pt;
                        font-weight: bold;
                        margin: 30px 0 20px 0;
                        text-transform: uppercase;
                        letter-spacing: 1px;
                    }
                    .subtitulo {
                        margin: 20px 0 10px 0;
                        font-weight: bold;
                    }
                    .cuerpo {
                        text-align: justify;
                        margin: 20px 0;
                        line-height: 1.8;
                    }
                    .cuerpo strong {
                        font-weight: bold;
                    }
                    .saludo {
                        margin: 20px 0;
                    }
                    .firma-section {
                        margin-top: 40px;
                        position: relative;
                        text-align: center;
                    }
                    .sello-box {
                        position: absolute;
                        right: 0;
                        top: 0;
                        width: 230px;
                        height: 173px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                    }
                    .sello-box img {
                        max-width: 100%;
                        max-height: 100%;
                        object-fit: contain;
                    }
                    .presidente-nombre {
                        font-weight: bold;
                        margin-top: 5px;
                    }
                    .presidente-cargo {
                        font-size: 10pt;
                        margin-top: 5px;
                    }
                    .footer {
                        margin-top: 50px;
                        font-size: 10pt;
                        text-align: center;
                    }
                    @media print {
                        body { padding: 20px; }
                    }
                </style>
            </head>
            <body>
                <div class="header">
                    ${logoUrl ? `<img src="${logoUrl}" alt="Logo" class="logo" />` : '<div></div>'}
                    <div class="fecha">${constancia.fecha}</div>
                </div>
                
                <div class="titulo">${constancia.titulo}</div>
                
                <div class="subtitulo">${constancia.subtitulo}</div>
                
                <div class="cuerpo">
                    ${constancia.textoCuerpo}
                </div>
                
                <div class="saludo">
                    Sin más por el momento, reciba un cordial saludo.
                </div>
                
                <div class="saludo">
                    Atentamente:
                </div>
                
                <div class="firma-section">
                    <div style="display: inline-flex; flex-direction: column; align-items: center;">
                        ${firmaUrl ? `<img src="${firmaUrl}" alt="Firma" style="max-width: 360px; max-height: 144px; object-fit: contain; margin-bottom: 10px;" />` : ''}
                        <div class="presidente-nombre">C. ${constancia.presidente}</div>
                        <div class="presidente-cargo">${constancia.cargo}</div>
                    </div>
                    ${selloUrl ? `<div class="sello-box"><img src="${selloUrl}" alt="Sello" /></div>` : ''}
                </div>
                
                <div class="footer">
                    ${constancia.nombreFracc}<br>
                    ${constancia.direccion}
                </div>
                
                <script>
                    window.onload = function() {
                        // Esperar a que el contenido esté montado y dibujado antes de imprimir.
                        setTimeout(function() {
                            window.print();
                        }, 500); 
                    };
                </script>
            </body>
            </html>
        `;

        printWindow.document.write(htmlContent);
        printWindow.document.close();
        printWindow.focus();
    };

    const constancia = generarConstancia();

    return (
        <div className="constancia-page">
            <div className="constancia-header-wrapper">
                <header className="constancia-header">
                    <div className="constancia-header-icon">
                        <FaFileAlt />
                    </div>
                    <div>
                        <h2>Constancia de No Adeudo</h2>
                        <p>Genera constancias de no adeudo para residentes al corriente</p>
                    </div>
                    <button onClick={() => navigate('/administracion')} className="constancia-back-link">
                        ← Regresar a administración
                    </button>
                </header>
            </div>

            <div className="constancia-content">
                <div className="constancia-top-section">
                    <div className="constancia-card">
                        <h3>Seleccionar Residente</h3>
                        {/* ... search bar ... */}
                        <div className="search-bar">
                            <div className="search-input-wrapper">
                                <FaSearch className="search-icon" />
                                <input
                                    type="text"
                                    placeholder="Buscar por número de casa o nombre del residente..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                        </div>

                        {loading ? (
                            <div className="loading-message">Cargando residentes...</div>
                        ) : filteredCasas.length === 0 ? (
                            <div className="empty-message">
                                {searchTerm ? 'No se encontraron residentes con ese criterio' : 'No hay residentes al corriente'}
                            </div>
                        ) : (
                            <div className="casas-list">
                                {filteredCasas.map(casa => (
                                    <div
                                        key={casa.id}
                                        className={`casa-item ${selectedCasa?.id === casa.id ? 'selected' : ''}`}
                                        onClick={() => handleCasaSelect(casa)}
                                    >
                                        <div className="casa-info">
                                            <span className="casa-numero">Casa {casa.numero}</span>
                                            <span className="casa-residente">{getResidentName(casa)}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="constancia-card">
                        <h3>Configuración del Documento</h3>

                        <div className="form-group">
                            <label>Ciudad y Estado</label>
                            <input
                                type="text"
                                value={ciudadEstado}
                                onChange={(e) => setCiudadEstado(e.target.value)}
                                placeholder="Ej: San Juan del Río Querétaro"
                            />
                        </div>

                        <div className="form-group">
                            <label>Fecha del Documento</label>
                            <input
                                type="date"
                                className="styled-date-input"
                                onClick={(e) => {
                                    try {
                                        if (e.target.showPicker) e.target.showPicker();
                                    } catch (err) {
                                        // Ignore en navegadores no soportados
                                    }
                                }}
                                value={`${safeDate(fechaConstancia).getFullYear()}-${String(safeDate(fechaConstancia).getMonth() + 1).padStart(2, '0')}-${String(safeDate(fechaConstancia).getDate()).padStart(2, '0')}`}
                                onChange={(e) => {
                                    const [year, month, day] = e.target.value.split('-').map(Number);
                                    const localDate = new Date(year, month - 1, day);
                                    setFechaConstancia(localDate);
                                    guardarConfiguracionConstancia();
                                }}
                            />
                        </div>

                        {/* ... other form groups ... */}
                        <div className="form-group">
                            <label>Título del Documento</label>
                            <input
                                type="text"
                                value={tituloConstancia}
                                onChange={(e) => {
                                    setTituloConstancia(e.target.value);
                                    guardarConfiguracionConstancia();
                                }}
                                placeholder="Ej: CONSTANCIA DE NO ADEUDO"
                            />
                        </div>

                        <div className="form-group">
                            <label>A Quién Va Dirigida</label>
                            <input
                                type="text"
                                value={subtituloConstancia}
                                onChange={(e) => {
                                    setSubtituloConstancia(e.target.value);
                                    guardarConfiguracionConstancia();
                                }}
                                placeholder="Ej: A QUIEN CORRESPONDA:"
                            />
                        </div>

                        <div className="form-group">
                            <label>Nombre del Presidente</label>
                            <input
                                type="text"
                                value={presidenteNombre}
                                onChange={(e) => setPresidenteNombre(e.target.value)}
                                placeholder="Nombre del presidente"
                            />
                        </div>

                        <div className="form-group">
                            <label>Cargo del Presidente</label>
                            <input
                                type="text"
                                value={presidenteCargo}
                                onChange={(e) => setPresidenteCargo(e.target.value)}
                                placeholder="Presidente y Administrador del Fraccionamiento"
                            />
                        </div>

                        <div className="form-group">
                            <label>Dirección del Fraccionamiento</label>
                            <input
                                type="text"
                                value={direccionFraccionamiento}
                                onChange={(e) => setDireccionFraccionamiento(e.target.value)}
                                placeholder="Dirección completa"
                            />
                        </div>

                        <div className="form-group">
                            <label>Redacción de la Carta</label>
                            <div className="redaccion-container">
                                <textarea
                                    id="redaccion-textarea"
                                    rows="6"
                                    value={resolveTemplate(redaccionConstancia || DEFAULT_REDACCION)}
                                    readOnly={true}
                                    placeholder=""
                                    className="redaccion-textarea disable-scroll"
                                    style={{ backgroundColor: '#f8fafc', color: '#64748b', cursor: 'default' }}
                                />
                                <small className="redaccion-hint">
                                    Visualización de la redacción actual. Para modificarla, use el botón "Abrir Editor".
                                </small>
                                <button
                                    type="button"
                                    className="expand-editor-btn"
                                    onClick={() => setEditorModalOpen(true)}
                                    title="Abrir editor expandido"
                                >
                                    <FaExpand /> Abrir Editor
                                </button>
                            </div>
                        </div>

                        <div className="upload-section">
                            {/* ... upload items ... */}
                            <div className="upload-item">
                                <label className="upload-label">
                                    <FaUpload /> Cargar Firma de Administrador
                                </label>
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleFirmaUpload}
                                    disabled={uploadingFirma}
                                    style={{ display: 'none' }}
                                    id="firma-upload"
                                />
                                <label htmlFor="firma-upload" className="upload-button">
                                    {uploadingFirma ? 'Subiendo...' : firmaUrl ? 'Firma cargada ✓' : 'Seleccionar archivo'}
                                </label>
                                {firmaUrl && (
                                    <button
                                        className="remove-button"
                                        onClick={() => handleRemoveImage('firma')}
                                        title="Eliminar firma"
                                    >
                                        <FaTimes />
                                    </button>
                                )}
                            </div>

                            <div className="upload-item">
                                <label className="upload-label">
                                    <FaUpload /> Cargar Sello de Verificación
                                </label>
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleSelloUpload}
                                    disabled={uploadingSello}
                                    style={{ display: 'none' }}
                                    id="sello-upload"
                                />
                                <label htmlFor="sello-upload" className="upload-button">
                                    {uploadingSello ? 'Subiendo...' : selloUrl ? 'Sello cargado ✓' : 'Seleccionar archivo'}
                                </label>
                                {selloUrl && (
                                    <button
                                        className="remove-button"
                                        onClick={() => handleRemoveImage('sello')}
                                        title="Eliminar sello"
                                    >
                                        <FaTimes />
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="constancia-preview-section">
                    <div className="constancia-card preview-card">
                        <div className="preview-header">
                            <h3>Vista Previa</h3>
                            <button
                                className="print-button"
                                onClick={handlePrint}
                                disabled={!constancia}
                            >
                                <FaPrint /> Imprimir/Exportar
                            </button>
                        </div>

                        {constancia ? (
                            <div className="preview-content">
                                <div className="preview-document">
                                    {/* ... document preview structure ... */}
                                    <div className="preview-header-doc">
                                        {logoUrl && (
                                            <img src={logoUrl} alt="Logo" className="preview-logo" />
                                        )}
                                        <div className="preview-fecha">{constancia.fecha}</div>
                                    </div>

                                    <div className="preview-titulo">{constancia.titulo}</div>

                                    <div className="preview-subtitulo">{constancia.subtitulo}</div>

                                    <div className="preview-cuerpo">
                                        {constancia.textoCuerpo}
                                    </div>

                                    <div className="preview-saludo">
                                        Sin más por el momento, reciba un cordial saludo.
                                    </div>

                                    <div className="preview-saludo">
                                        Atentamente:
                                    </div>

                                    <div className="preview-firma-section">
                                        <div className="preview-firma-container">
                                            <div className="preview-firma-left">
                                                {firmaUrl && (
                                                    <img src={firmaUrl} alt="Firma" className="preview-firma-img" />
                                                )}
                                                <div className="preview-presidente-nombre">C. {constancia.presidente}</div>
                                                <div className="preview-presidente-cargo">{constancia.cargo}</div>
                                            </div>
                                            {selloUrl && (
                                                <div className="preview-sello-box">
                                                    <img src={selloUrl} alt="Sello" className="preview-sello-img" />
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="preview-footer">
                                        {constancia.nombreFracc}<br />
                                        {constancia.direccion}
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="preview-empty">
                                <p>Selecciona un residente para ver la vista previa</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Modal del Editor de Texto Enriquecido */}
            {editorModalOpen && (
                <div className="editor-modal-overlay" onClick={() => setEditorModalOpen(false)}>
                    <div className="editor-modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="editor-modal-header">
                            <h3>Editor de Redacción</h3>
                            <button
                                className="editor-close-btn"
                                onClick={() => setEditorModalOpen(false)}
                            >
                                <FaTimes />
                            </button>
                        </div>

                        <div className="editor-toolbar">
                            {/* ... toolbar buttons ... */}
                            <div className="toolbar-group">
                                <label>Tamaño de Fuente:</label>
                                <select
                                    value={fontSize}
                                    onChange={(e) => {
                                        setFontSize(Number(e.target.value));
                                        guardarConfiguracionConstancia();
                                    }}
                                    className="font-size-select"
                                >
                                    <option value="10">10pt</option>
                                    <option value="11">11pt</option>
                                    <option value="12">12pt</option>
                                    <option value="14">14pt</option>
                                    <option value="16">16pt</option>
                                    <option value="18">18pt</option>
                                </select>
                            </div>

                            <div className="toolbar-group">
                                <label>Fuente:</label>
                                <select
                                    value={fontFamily}
                                    onChange={(e) => {
                                        setFontFamily(e.target.value);
                                        guardarConfiguracionConstancia();
                                    }}
                                    className="font-family-select"
                                >
                                    <option value="Times New Roman">Times New Roman</option>
                                    <option value="Arial">Arial</option>
                                    <option value="Helvetica">Helvetica</option>
                                    <option value="Georgia">Georgia</option>
                                    <option value="Courier New">Courier New</option>
                                </select>
                            </div>

                            <div className="toolbar-separator"></div>

                            <button
                                type="button"
                                className="toolbar-btn"
                                onClick={() => handleFormatCommand('bold')}
                                title="Negrita"
                            >
                                <FaBold />
                            </button>
                            <button
                                type="button"
                                className="toolbar-btn"
                                onClick={() => handleFormatCommand('italic')}
                                title="Cursiva"
                            >
                                <FaItalic />
                            </button>
                            <button
                                type="button"
                                className="toolbar-btn"
                                onClick={() => handleFormatCommand('underline')}
                                title="Subrayado"
                            >
                                <FaUnderline />
                            </button>

                            <div className="toolbar-separator"></div>

                            <button
                                type="button"
                                className="toolbar-btn"
                                onClick={() => handleFormatCommand('justifyLeft')}
                                title="Alinear Izquierda"
                            >
                                <FaAlignLeft />
                            </button>
                            <button
                                type="button"
                                className="toolbar-btn"
                                onClick={() => handleFormatCommand('justifyCenter')}
                                title="Centrar"
                            >
                                <FaAlignCenter />
                            </button>
                            <button
                                type="button"
                                className="toolbar-btn"
                                onClick={() => handleFormatCommand('justifyRight')}
                                title="Alinear Derecha"
                            >
                                <FaAlignRight />
                            </button>
                        </div>

                        <div className="editor-textarea-container">
                            <div
                                id="editor-content"
                                contentEditable
                                ref={editorRef}
                                onInput={(e) => {
                                    setRedaccionConstancia(e.target.innerText);
                                }}
                                onBlur={guardarConfiguracionConstancia}
                                className="editor-textarea"
                                style={{
                                    fontSize: `${fontSize}pt`,
                                    fontFamily: fontFamily
                                }}
                                suppressContentEditableWarning={true}
                            >
                            </div>
                        </div>

                        <div className="editor-modal-footer">
                            <button
                                className="editor-save-btn"
                                onClick={() => {
                                    guardarConfiguracionConstancia();
                                    setEditorModalOpen(false);
                                }}
                            >
                                Guardar y Cerrar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ConstanciaNoAdeudo;
